import { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/Badge';
import { NumberInput } from '../ui/NumberInput';
import { btnSecondary } from '../ui/styles';
import { money } from '../../core/format/currency';
import { patchStockItem } from '../adminApi';
import { errorMessage } from '../../core/errors';
import { locationLabel } from '../../core/rules/stockMovements';
import { stockLocations, type StockItem, type StockMovementType } from '../../core/types/stock';
import { StockActionForm } from './StockActionForm';
import { StockHistory } from './StockHistory';
type Props = {
  item: StockItem;
  open: boolean;
  onToggle: () => void;
  onChange: (item: StockItem) => void;
};

/** Knoppen in volgorde van hoe vaak ze nodig zijn. Plek en richting kies je in het formulier. */
const actions: {
  type: StockMovementType;
  label: string;
}[] = [{
  type: 'overboeken',
  label: 'Overboeken'
}, {
  type: 'verbruik',
  label: 'In gebruik genomen'
}, {
  type: 'inkoop',
  label: 'Inkoop ontvangen'
}, {
  type: 'afschrijven',
  label: 'Afschrijven'
}, {
  type: 'telling',
  label: 'Tellen'
}];
const levelText = (v: number | null) => v === null ? '—' : String(v);

/** Eén artikel: standen per plek, verkopen van de laatste 30 dagen, en open: acties, inkoopprijs en historie. */
export function StockRow({
  item,
  open,
  onToggle,
  onChange
}: Props) {
  const [action, setAction] = useState<StockMovementType | null>(null);
  const [version, setVersion] = useState(0);
  const panelId = `voorraad-${item.id}`;
  const done = (next: StockItem) => {
    setAction(null);
    setVersion(v => v + 1);
    onChange(next);
  };
  return <li data-id="element-4819" data-mp="mfvn8b9arb01">
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={panelId} className="grid w-full grid-cols-[minmax(0,1fr)_auto_auto_16px] items-center gap-x-4 px-4 py-3 text-left transition-colors duration-150 ease-soft hover:bg-shell sm:grid-cols-[minmax(0,1fr)_72px_72px_120px_16px] sm:px-5" data-id="element-4820" data-mp="rgt1wdv3lmws">
        <span className="min-w-0" data-id="element-4821" data-mp="2l804yb6b7tbi">
          <span className="block truncate text-[14px] text-ink" data-id="element-4822" data-mp="2g8evv7xfe4l2">{item.name}</span>
          <span className="block truncate text-[12px] text-muted" data-id="element-4823" data-mp="2gahx6o413k53">
            {item.brand ?? (item.kind === 'verbruik' ? 'Verbruik' : 'Product')}
            {item.kind === 'verbruik' && item.brand && ' · verbruik'}
          </span>
        </span>
        {stockLocations.map(l => {
        const v = item.levels[l];
        return <span key={l} className="text-right" data-id="element-4824" data-mp="2z7sqzt6w5ld5">
              <span className="block text-[11px] text-muted sm:hidden" data-id="element-4825" data-mp="2z56nc4n4fq07">{locationLabel[l]}</span>
              <span className={`text-[14px] tabular-nums ${v !== null && v <= 0 ? 'text-clay' : 'text-ink'}`} data-id="element-4826" data-mp="3dvdys0epo8re">{levelText(v)}</span>
            </span>;
      })}
        <span className="hidden text-right text-[12px] tabular-nums text-muted sm:block" data-id="element-4827" data-mp="3mxvfy0fieb7j">
          {item.kind === 'retail' ? `${item.sold30.webshop} web · ${item.sold30.salon} salon` : '—'}
        </span>
        <ChevronDownIcon className={`h-4 w-4 text-muted transition-transform duration-150 ease-soft ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-4828" data-mp="5fg56pwhyis8" />
      </button>

      {open && <div id={panelId} className="space-y-5 border-t border-line bg-shell px-4 py-4 sm:px-5" data-id="element-4829" data-mp="3si2vxvlhq9um">
          <dl className="grid grid-cols-2 gap-4 text-[13px] sm:grid-cols-4" data-id="element-4830" data-mp="1b8iszdihex6">
            {stockLocations.map(l => <div key={l} data-id="element-4831" data-mp="3iqyemp8cecwm">
                <dt className="text-muted" data-id="element-4832" data-mp="3njhrtl34b60w">{locationLabel[l]}</dt>
                <dd className="text-[16px] tabular-nums text-ink" data-id="element-4833" data-mp="3nl2nxf52yx0v">
                  {levelText(item.levels[l])}
                  {item.levels[l] !== null && (item.levels[l] ?? 0) < 0 && <span className="ml-2 text-[12px] text-clay" data-id="element-4834" data-mp="3duzsz9eef8zw">tel opnieuw</span>}
                </dd>
              </div>)}
            {item.kind === 'retail' && <div className="col-span-2" data-id="element-4835" data-mp="34ttfl971qeav">
                <dt className="text-muted" data-id="element-4836" data-mp="34si8p0ib0qfj">Verkocht, laatste 30 dagen</dt>
                <dd className="text-ink" data-id="element-4837" data-mp="b08w93in0iwh">
                  <span className="tabular-nums" data-id="element-4838" data-mp="b34s8maxcxia">{item.sold30.webshop}</span> via de webshop · <span className="tabular-nums" data-id="element-4839" data-mp="708d55klfjj3">{item.sold30.salon}</span> in de salon
                </dd>
              </div>}
          </dl>
          {item.kind === 'retail' && <p className="text-[12px] text-muted" data-id="element-4840" data-mp="3tersb4he72qm">De webshop verkoopt alleen uit de webshopvoorraad: staat die op 0, dan is het product online uitverkocht, ook als de salon er nog heeft.</p>}

          <CostPrice item={item} onChange={onChange} data-id="element-4841" data-mp="3jfk7jw8bw5th" />

          {action ? <StockActionForm item={item} type={action} onDone={done} onCancel={() => setAction(null)} data-id="element-4842" data-mp="3fgjsmosz1nbr" /> : <div className="flex flex-wrap gap-2" role="group" aria-label={`Acties voor ${item.name}`} data-id="element-4843" data-mp="3ai05il2n65ht">
              {actions.map(a => <button key={a.type} type="button" onClick={() => setAction(a.type)} className={btnSecondary} data-id="element-4844" data-mp="18pbvnsvhsjt2">
                  {a.label}
                </button>)}
            </div>}

          <section aria-label="Historie" data-id="element-4845" data-mp="1l74rdnmi7wb0">
            <h3 className="mb-1 text-[13px] font-medium text-ink" data-id="element-4846" data-mp="1l63h3ldbbduc">Historie</h3>
            <StockHistory key={version} itemId={item.id} data-id="element-4847" data-mp="1pzft8w0t1s67" />
          </section>
        </div>}
    </li>;
}
function CostPrice({
  item,
  onChange
}: {
  item: StockItem;
  onChange: (item: StockItem) => void;
}) {
  const saved = item.costPriceCents === undefined ? undefined : item.costPriceCents / 100;
  const [value, setValue] = useState(saved);
  const [busy, setBusy] = useState(false);
  const changed = value !== saved;
  const save = async () => {
    setBusy(true);
    try {
      const next = await patchStockItem(item.id, {
        costPriceCents: value === undefined ? null : Math.round(value * 100)
      });
      onChange(next);
      toast.success('Inkoopprijs opgeslagen');
    } catch (err) {
      toast.error('Opslaan lukte niet', {
        description: errorMessage(err) ?? 'Probeer het zo nog eens.'
      });
    } finally {
      setBusy(false);
    }
  };
  return <div className="flex flex-wrap items-end gap-2" data-id="element-4848" data-mp="iksxdakq4cod">
      <label className="block" data-id="element-4849" data-mp="ik12k1yyaiz5">
        <span className="mb-1 block text-[12px] text-muted" data-id="element-4850" data-mp="in5anafjdyv3">Inkoopprijs per stuk (€, excl. btw)</span>
        <NumberInput decimals allowEmpty value={value} onChange={setValue} placeholder="Onbekend" className="w-32" data-id="element-4851" data-mp="wi7oqkujeuiz" />
      </label>
      {changed && <button type="button" onClick={() => void save()} disabled={busy} className={btnSecondary} data-id="element-4852" data-mp="3wxzlfruzi4f">
          {busy ? 'Bezig…' : 'Opslaan'}
        </button>}
      {!changed && saved !== undefined && <Badge tone="neutral" data-id="element-4853" data-mp="dmraeek6ft7m">{money(item.costPriceCents ?? 0)}</Badge>}
    </div>;
}